import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import AppHeader from "../components/AppHeader";
import DisclaimerBanner from "../components/DisclaimerBanner";
import SiteFooter from "../components/SiteFooter";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  ArrowUpRight,
  ChevronLeft,
  ExternalLink,
  Info,
  Landmark,
  Search,
  Phone,
  HandHeart,
} from "lucide-react";

const inputCls =
  "rounded-none border-slate-300 focus:border-slate-900 focus:ring-1 focus:ring-slate-900";

export default function FreeLegalHelp() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/free-legal-help");
        setEntries(r.data);
      } catch (e) {
        toast.error("Could not load legal help directory");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const { federal, states } = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filter = (e) =>
      !q ||
      e.jurisdiction_name.toLowerCase().includes(q) ||
      e.code.toLowerCase().includes(q) ||
      (e.primary_org_name || "").toLowerCase().includes(q) ||
      (e.focus_areas || []).some((f) => f.toLowerCase().includes(q));
    return {
      federal: entries.filter((e) => e.scope === "federal").filter(filter),
      states: entries.filter((e) => e.scope !== "federal").filter(filter),
    };
  }, [entries, query]);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <DisclaimerBanner />
      <AppHeader />
      <main className="max-w-[1300px] mx-auto px-6 py-10 md:py-14 flex-1 w-full" data-testid="free-legal-help-page">
        <div className="flex items-center gap-4 mb-6">
          <Link
            to="/resources"
            className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900 underline underline-offset-4"
            data-testid="back-to-resources"
          >
            <ChevronLeft className="w-4 h-4" /> Back to Resources
          </Link>
        </div>
        <header className="mb-10 max-w-3xl">
          <div className="label-tiny mb-3">Directory</div>
          <h1 className="font-serif-display text-4xl md:text-5xl text-slate-900 leading-tight">
            <HandHeart className="inline-block w-9 h-9 mr-2 -mt-2 text-orange-600" strokeWidth={1.75} />
            Free Legal Help
          </h1>
          <p className="mt-4 text-slate-600">
            If you cannot afford an attorney, the organizations below provide free civil
            legal help to eligible residents. Most are Legal Services Corporation (LSC)
            grantees. Intake is usually by phone or an online screening form; priorities
            typically include eviction defense, family violence, veterans, seniors, and
            public benefits.
          </p>
        </header>

        <div
          className="border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900 flex gap-3 mb-8"
          data-testid="free-legal-help-notice"
        >
          <Info className="w-5 h-5 mt-0.5 shrink-0 text-amber-700" />
          <div>
            ProSe.Claims provides links only. Eligibility rules and case-type coverage vary
            by organization and change over time. Confirm current criteria directly with the
            program before relying on any information here. If your matter is time-sensitive
            (an eviction hearing, restraining order, etc.), call the intake line — do not
            wait for an online screening reply.
          </div>
        </div>

        <div className="border border-slate-200 p-6 mb-10" data-testid="free-legal-help-search-card">
          <Label className="label-tiny mb-2 block">Find your state</Label>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by state, code, organization, or focus area (e.g. housing)"
              className={`${inputCls} pl-9`}
              data-testid="free-legal-help-search-input"
            />
          </div>
          <div className="text-xs text-slate-500 mt-2">
            {loading
              ? "Loading…"
              : `${federal.length + states.length} of ${entries.length} entries`}
          </div>
        </div>

        {federal.length > 0 && (
          <section className="mb-14" data-testid="free-legal-help-federal">
            <div className="flex items-center gap-2 mb-4">
              <Landmark className="w-5 h-5 text-slate-700" />
              <h2 className="font-serif-display text-2xl text-slate-900">National directory</h2>
            </div>
            <div className="grid grid-cols-1">
              {federal.map((e) => (
                <FreeLegalHelpCard key={e.code} entry={e} />
              ))}
            </div>
          </section>
        )}

        <section data-testid="free-legal-help-states">
          <h2 className="font-serif-display text-2xl text-slate-900 mb-4">States and D.C.</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
            {states.map((e) => (
              <FreeLegalHelpCard key={e.code} entry={e} />
            ))}
          </div>
          {!loading && federal.length + states.length === 0 && (
            <div className="border border-slate-200 p-10 text-center text-slate-500" data-testid="free-legal-help-empty">
              No entries match your search.
            </div>
          )}
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}

function FreeLegalHelpCard({ entry: e }) {
  return (
    <div
      className="group border border-slate-200 -mt-px -ml-px bg-white p-5 md:p-6 transition-shadow duration-200 hover:z-10 hover:shadow-[0_6px_18px_-12px_rgba(15,23,42,0.15)] flex flex-col"
      data-testid={`free-legal-help-card-${e.code}`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <div className="font-mono-tech text-[10px] tracking-widest text-slate-500 uppercase">
            {e.code}
          </div>
          <div className="font-serif-display text-xl text-slate-900 leading-tight mt-1 truncate">
            {e.jurisdiction_name}
          </div>
        </div>
        <Badge variant="outline" className="rounded-none border-emerald-300 bg-emerald-50 text-emerald-800 text-[10px] font-mono-tech shrink-0">
          Free
        </Badge>
      </div>

      <div className="text-sm text-slate-800">
        <span className="label-tiny">Primary organization</span>
        <div className="font-medium text-slate-900 leading-snug">{e.primary_org_name}</div>
      </div>

      {(e.focus_areas || []).length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-3">
          {e.focus_areas.map((f) => (
            <span
              key={f}
              className="text-[10px] font-mono-tech uppercase tracking-wider text-slate-600 border border-slate-300 px-1.5 py-0.5"
            >
              {f}
            </span>
          ))}
        </div>
      )}

      <p className="text-sm text-slate-700 leading-relaxed mt-3 flex-1">{e.plain_english}</p>

      {e.who_qualifies && (
        <div className="mt-3 text-xs text-slate-600 border-l-2 border-slate-300 pl-3">
          {e.who_qualifies}
        </div>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <a
          href={e.primary_org_url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-sm font-medium text-white bg-slate-900 hover:bg-slate-800 px-3 py-1.5"
          data-testid={`open-legal-help-${e.code}`}
        >
          Get help <ArrowUpRight className="w-3.5 h-3.5" />
        </a>
        {e.hotline && (
          <a
            href={`tel:${e.hotline.replace(/[^+\d]/g, "")}`}
            className="inline-flex items-center gap-1 text-sm font-medium text-slate-900 border border-slate-300 hover:bg-slate-50 px-3 py-1.5"
            data-testid={`call-legal-help-${e.code}`}
          >
            <Phone className="w-3.5 h-3.5" /> {e.hotline}
          </a>
        )}
        {e.portal_url && (
          <a
            href={e.portal_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900 underline underline-offset-4 ml-auto"
            data-testid={`open-portal-${e.code}`}
          >
            Self-help portal <ExternalLink className="w-3 h-3" />
          </a>
        )}
      </div>
      <div className="text-[11px] text-slate-500 mt-3">
        Last verified: <span className="tabular-nums">{e.last_verified_at || "—"}</span>
      </div>
    </div>
  );
}
